import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Icon, Theme } from '..';
import { flattenDataWith, sortDataBy, isRoot } from './helpers';
import { Body, RowHeader, RowHeaderContent, Row, TextEllipsis } from './elements';

var TableBody = function TableBody(_ref) {
  var bodyRef = _ref.bodyRef,
      colsDef = _ref.colsDef,
      data = _ref.data,
      handleRowClick = _ref.handleRowClick,
      hasClickCallback = _ref.hasClickCallback,
      isHoverable = _ref.isHoverable,
      isScrollable = _ref.isScrollable,
      sort = _ref.sort,
      striped = _ref.striped,
      unfoldedRows = _ref.unfoldedRows;
  var sortBy = useCallback(sortDataBy(colsDef, sort), [colsDef, sort]);
  var flatten = useCallback(flattenDataWith(sortBy), [sortBy]);
  var flatData = useMemo(function () {
    return flatten(data);
  }, [flatten, data]);
  return /*#__PURE__*/React.createElement(Body, {
    colsLength: colsDef.length + 1,
    ref: bodyRef
  }, flatData.map(function (_ref2) {
    var depth = _ref2.depth,
        key = _ref2.key,
        item = _ref2.item,
        parent = _ref2.parent;
    return (isRoot(parent) || unfoldedRows.includes(parent)) && /*#__PURE__*/React.createElement(Row, {
      key: key,
      "data-testid": "body-row",
      isClickable: item.children || hasClickCallback,
      isHoverable: isHoverable,
      isUnfolded: unfoldedRows.includes(key),
      onClick: function onClick() {
        return handleRowClick(item, key);
      },
      striped: striped,
      depth: depth
    }, colsDef.map(function (_ref3) {
      var name = _ref3.name,
          isRowHeader = _ref3.isRowHeader,
          value = _ref3.value,
          format = _ref3.format,
          align = _ref3.align;
      return isRowHeader ? /*#__PURE__*/React.createElement(RowHeader, {
        align: align,
        isScrollable: isScrollable,
        key: "".concat(key, "-").concat(name),
        hasChildren: !!item.children
      }, /*#__PURE__*/React.createElement(RowHeaderContent, null, item.children && /*#__PURE__*/React.createElement(Icon, {
        name: unfoldedRows.includes(key) ? 'chevron-down' : 'chevron-right',
        color: Theme.palette.darkBlue
      }), /*#__PURE__*/React.createElement(TextEllipsis, null, format ? format(value(item, key), key) : value(item, key)))) : /*#__PURE__*/React.createElement("td", {
        key: "".concat(key, "-").concat(name),
        align: align
      }, format ? format(value(item, key), key) : value(item, key));
    }));
  }));
};

var array = PropTypes.array,
    arrayOf = PropTypes.arrayOf,
    bool = PropTypes.bool,
    func = PropTypes.func,
    node = PropTypes.node,
    number = PropTypes.number,
    object = PropTypes.object,
    oneOf = PropTypes.oneOf,
    oneOfType = PropTypes.oneOfType,
    shape = PropTypes.shape,
    string = PropTypes.string;
/** Prop types. */

TableBody.propTypes = {
  bodyRef: object.isRequired,
  colsDef: arrayOf(shape({
    align: oneOf(['left', 'center', 'right']),
    isRowHeader: bool,
    filteredBy: func,
    format: func,
    isSortable: bool,
    title: node.isRequired,
    total: func,
    value: func.isRequired,
    width: oneOfType([string, number])
  })).isRequired,
  data: array.isRequired,
  handleRowClick: func.isRequired,
  hasClickCallback: bool.isRequired,
  isHoverable: bool,
  isScrollable: bool,
  sort: shape({
    column: string,
    order: oneOf(['asc', 'desc'])
  }),
  striped: bool,
  unfoldedRows: array.isRequired
};
/** Default props. */

TableBody.defaultProps = {
  isHoverable: false,
  isScrollable: false,
  sort: null,
  striped: false
};
export default React.memo(TableBody);